import {
    IJob,
    IPositionBenefit,
    IPositionChip,
    IPositionChipCategory,
    JobStatus,
} from '../../../common';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Icon,
    Typography,
    useTheme,
    IconButton,
    Chip,
    useAppSelector,
    useAppDispatch,
} from '../../../base';
import { useTranslation } from 'react-i18next';
import {
    ChipsContainer,
    StyledLabel,
} from './JobRequirementsAndBenefitsContent.styles';
import { Fragment } from 'react/jsx-runtime';
import { jobsActions, jobsSelectors } from '../../../actions';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../../routes/RoutePaths';

interface Props {
    isEmployerView?: boolean;
    isPreview?: boolean;
}

const JobRequirementsAndBenefitsContent = ({
    isEmployerView = false,
    isPreview = false,
}: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const job: IJob =
        useAppSelector(jobsSelectors.getActiveJob) || ({} as IJob);
    const stepperInfo = useAppSelector(jobsSelectors.getJobStepperInfo);

    // Internal legend configuration
    const legendItems = [
        {
            color: theme.palette.success.main,
            text: t('positionPage.mandatory'),
        },
        {
            color: theme.palette.warning.main,
            text: t('positionPage.advantage'),
        },
    ];

    const getRequirementGroupSectionIcon = (groupName: string) => {
        switch (groupName) {
            case 'Technical Skills':
                return 'HandymanOutlinedIcon';
            case 'Educational Requirements':
                return 'SchoolOutlinedIcon';
            case 'Soft Skills':
                return 'StarBorderRoundedIcon';
            default:
                return 'StarBorderRoundedIcon';
        }
    };

    const requirementsChips: Array<IPositionChipCategory> | undefined =
        job.jobRequirementGroups?.map((group) => ({
            sectionTitle: group.groupName,
            sectionIconName: getRequirementGroupSectionIcon(group.groupName),
            chipsData: group.requirements.map((requirment) => ({
                title: requirment.name,
                importanceLevel: requirment.importanceLevel,
                rangeFrom: requirment.rangeFrom,
                rangeTo: requirment.rangeTo,
            })),
        }));

    // const benefitChips: Array<IPositionBenefit> =
    //     job.jobBenefits?.map((benefit) => ({
    //         title:
    //             benefit.jobBenefitsValue?.name ||
    //             benefit.jobBenefitsOption?.name,
    //     })) || [];

    const renderChips = (chips: Array<IPositionChip>) => {
        return chips?.map((chip, index) => {
            let chipColor;

            if (chip.importanceLevel === 'MANDATORY') {
                chipColor = theme.palette.success.main;
            } else if (chip.importanceLevel === 'ADVANTAGE') {
                chipColor = theme.palette.warning.main;
            } else {
                chipColor = undefined; // Use background color if neither
            }

            // Start of Selection
            return (
                <StyledLabel key={index}>
                    {chipColor && (
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: chipColor,
                                marginInlineEnd: theme.spacing(1),
                            }}
                        />
                    )}
                    <Typography variant={'body2'} sx={{ fontWeight: 'bold' }}>
                        {chip.title}
                        {(chip.rangeFrom || chip.rangeTo) &&
                            ` / ${chip.rangeFrom}${chip.rangeTo ? ` - ${chip.rangeTo}` : '+'} ${t('positionPage.years')}`}
                    </Typography>
                </StyledLabel>
            );
        });
    };

    const onEditClick = () => {
        dispatch(
            jobsActions.setStepperInfo({
                stepperInfo: {
                    ...stepperInfo,
                    activeStepIndex: 1,
                },
            })
        );
        if (!location.pathname.includes(RoutePaths.saveJob)) {
            navigate(`${location.pathname}/save-job${location.search}`);
        }
    };

    return (
        <Grid container direction={'column'}>
            <Grid item>
                <Card
                    sx={{
                        paddingTop: theme.spacing(3),
                        boxShadow: 'none',
                        border: isPreview
                            ? `1px solid ${theme.palette.grey[400]}`
                            : 'none',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto 1fr',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        {/* Title - Left */}
                        <Typography variant="h4" color="black">
                            {t('positionPage.requirements')}
                        </Typography>

                        {/* Legend - Center */}
                        <Grid
                            container
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                            sx={{ maxWidth: 'fit-content' }}
                        >
                            {legendItems.map((item, index) => (
                                <Grid item key={index}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: item.color,
                                            }}
                                        />
                                        <Typography variant="body2">
                                            {item.text}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Edit Button - Right */}
                        <Box display="flex" justifyContent="flex-end">
                            {(isEmployerView || isPreview) && job.status !== JobStatus.Legacy && (
                                <IconButton
                                    onClick={onEditClick}
                                    sx={{ marginInlineEnd: theme.spacing(-2) }}
                                >
                                    <Icon name={'EditIcon'} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>

                    <CardContent sx={{ paddingInlineStart: 0 }}>
                        {requirementsChips?.map((chipCategory, index) => (
                            <Fragment key={index}>
                                {chipCategory.sectionTitle && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        paddingBottom={theme.spacing(2)}
                                    >
                                        <Typography
                                            variant="h4"
                                            color={theme.palette.grey[600]}
                                        >
                                            {chipCategory.sectionTitle}
                                        </Typography>
                                    </Box>
                                )}
                                <ChipsContainer>
                                    {renderChips(chipCategory.chipsData || [])}
                                </ChipsContainer>
                            </Fragment>
                        ))}
                        {/* 
                        <Typography
                            variant="h4"
                            color="black"
                            sx={{
                                paddingTop: theme.spacing(6),
                                paddingBottom: theme.spacing(2),
                            }}
                        >
                            {t('positionPage.benefits')}
                        </Typography>
                        {benefitChips?.map((benefit, index) => (
                            // Start of Selection
                            <Chip
                                label={
                                    <Typography
                                        variant="body2"
                                        color={theme.palette.grey[600]}
                                    >
                                        {benefit.title}
                                    </Typography>
                                }
                                key={index}
                                sx={{
                                    marginInlineEnd: theme.spacing(1),
                                    backgroundColor: theme.palette.common.white,
                                    border: `1px solid`,
                                    borderColor: theme.palette.grey[600],
                                }}
                            />
                        ))} */}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default JobRequirementsAndBenefitsContent;
